import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { get, getPaginated, post, put, del } from '@/lib/api'

import type { ReimbursementType, ReimbursementStatus } from '@company/shared'


// ==================== Types ====================

export type { ReimbursementType, ReimbursementStatus }

export interface Reimbursement {
  id: string
  employeeId: string
  teamId: string | null
  projectId: string | null
  type: ReimbursementType
  amount: number
  currency: string
  description: string
  receipts: string[] | null
  status: ReimbursementStatus
  approverId: string | null
  approvedAt: string | null
  rejectedReason: string | null
  paidAt: string | null
  paidBy: string | null
  cashFlowId: string | null
  createdAt: string
  updatedAt: string | null
  deletedAt: string | null
}

export interface ReimbursementWithRelations extends Reimbursement {
  employee?: { id: string; name: string; employeeNo: string } | null
  team?: { id: string; name: string } | null
  project?: { id: string; name: string } | null
  approver?: { id: string; name: string } | null
}

export interface ReimbursementStatistics {
  totalAmount: number
  totalCount: number
  byType: Array<{ type: ReimbursementType; amount: number; count: number }>
  byStatus: Array<{ status: ReimbursementStatus; amount: number; count: number }>
  avgProcessingDays: number
}

export interface ReimbursementListParams {
  employeeId?: string
  teamId?: string
  projectId?: string
  type?: ReimbursementType
  status?: ReimbursementStatus
  startDate?: string
  endDate?: string
  page?: number
  pageSize?: number
}

export interface CreateReimbursementInput {
  type: ReimbursementType
  amount: number
  currency?: string
  description: string
  teamId?: string
  projectId?: string
}

export interface UpdateReimbursementInput {
  type?: ReimbursementType
  amount?: number
  description?: string
}

// ==================== Query Keys ====================

export const reimbursementKeys = {
  all: ['reimbursements'] as const,
  lists: () => [...reimbursementKeys.all, 'list'] as const,
  list: (params: ReimbursementListParams) => [...reimbursementKeys.lists(), params] as const,
  detail: (id: string) => [...reimbursementKeys.all, 'detail', id] as const,
  pending: () => [...reimbursementKeys.all, 'pending'] as const,
  statistics: (params: { startDate?: string; endDate?: string }) =>
    [...reimbursementKeys.all, 'statistics', params] as const,
}

// ==================== Hooks ====================

/**
 * 获取报销列表
 */
export function useReimbursements(params: ReimbursementListParams = {}) {
  return useQuery({
    queryKey: reimbursementKeys.list(params),
    queryFn: () =>
      getPaginated<ReimbursementWithRelations>(
        '/finance/reimbursements',
        params as Record<string, unknown>
      ),
  })
}

/**
 * 获取报销详情
 */
export function useReimbursement(id: string) {
  return useQuery({
    queryKey: reimbursementKeys.detail(id),
    queryFn: () => get<ReimbursementWithRelations>(`/finance/reimbursements/${id}`),
    enabled: !!id,
  })
}

/**
 * 获取待审批报销
 */
export function usePendingReimbursements() {
  return useQuery({
    queryKey: reimbursementKeys.pending(),
    queryFn: () => get<ReimbursementWithRelations[]>('/finance/reimbursements/pending'),
  })
}

/**
 * 获取报销统计
 */
export function useReimbursementStatistics(params: { startDate?: string; endDate?: string } = {}) {
  return useQuery({
    queryKey: reimbursementKeys.statistics(params),
    queryFn: () =>
      get<ReimbursementStatistics>(
        '/finance/reimbursements/statistics',
        params as Record<string, unknown>
      ),
  })
}

/**
 * 提交报销
 */
export function useCreateReimbursement() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateReimbursementInput) =>
      post<Reimbursement>('/finance/reimbursements', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reimbursementKeys.lists() })
    },
  })
}

/**
 * 更新报销
 */
export function useUpdateReimbursement() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateReimbursementInput }) =>
      put<Reimbursement>(`/finance/reimbursements/${id}`, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: reimbursementKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: reimbursementKeys.lists() })
    },
  })
}

/**
 * 取消报销
 */
export function useCancelReimbursement() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => del<null>(`/finance/reimbursements/${id}`),
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({ queryKey: reimbursementKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: reimbursementKeys.lists() })
    },
  })
}

/**
 * 审批通过
 */
export function useApproveReimbursement() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => post<Reimbursement>(`/finance/reimbursements/${id}/approve`),
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({ queryKey: reimbursementKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: reimbursementKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: reimbursementKeys.pending() })
    },
  })
}

/**
 * 审批拒绝
 */
export function useRejectReimbursement() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      post<Reimbursement>(`/finance/reimbursements/${id}/reject`, { reason }),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: reimbursementKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: reimbursementKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: reimbursementKeys.pending() })
    },
  })
}

/**
 * 标记已付款
 */
export function usePayReimbursement() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, accountId }: { id: string; accountId: string }) =>
      post<Reimbursement>(`/finance/reimbursements/${id}/pay`, { accountId }),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: reimbursementKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: reimbursementKeys.lists() })
    },
  })
}

/**
 * 批量付款
 */
export function useBatchPayReimbursements() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ ids, accountId }: { ids: string[]; accountId: string }) =>
      post<Reimbursement[]>('/finance/reimbursements/batch-pay', { ids, accountId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reimbursementKeys.lists() })
    },
  })
}

// ==================== Batch Approval Types ====================

export interface ReimbursementBatchApprovalResult {
  success: string[]
  failed: Array<{ id: string; reason: string }>
}

/**
 * 批量审批通过
 */
export function useBatchApproveReimbursements() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (ids: string[]) =>
      post<ReimbursementBatchApprovalResult>('/finance/reimbursements/batch-approve', { ids }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reimbursementKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: reimbursementKeys.pending() })
    },
  })
}

/**
 * 批量审批拒绝
 */
export function useBatchRejectReimbursements() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ ids, reason }: { ids: string[]; reason: string }) =>
      post<ReimbursementBatchApprovalResult>('/finance/reimbursements/batch-reject', { ids, reason }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reimbursementKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: reimbursementKeys.pending() })
    },
  })
}
